import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    UpdateFiservPaymentModuleDocument,
    type UpdateFiservPaymentModuleMutation,
    type UpdateFiservPaymentModuleMutationVariables,
} from '../../../../api/mutations/updateFiservPaymentModule';
import { type FiservPaymentModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = FiservPaymentModuleSettings;

type updateFiservPaymentModuleFormProps = {
    moduleId: string;
    initialValues: FormValues;
    onComplete: () => void;
};

const loadingMessageKey = 'update-fiserv-payment-module-loading';

const UpdateFiservPaymentModuleForm = ({ moduleId, initialValues, onComplete }: updateFiservPaymentModuleFormProps) => {
    const { t } = useTranslation(['fiservPaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('fiservPaymentModuleDetails:paymentModuleDrawer.messages.submitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateFiservPaymentModuleMutation, UpdateFiservPaymentModuleMutationVariables>({
                    mutation: UpdateFiservPaymentModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('fiservPaymentModuleDetails:paymentModuleDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateFiservPaymentModule"
                    layout="vertical"
                    name="updateFiservPaymentModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('fiservPaymentModuleDetails:paymentModuleDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateFiservPaymentModuleForm;
